import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import "./App.css";
import Main from "./containers/Main";
// Après Main : les surcharges sombres passent après le CSS des composants.
import "./theme-dark.css";
import { ThemeProvider } from "styled-components";
import { BaseProvider, LightTheme, DarkTheme } from "baseui";
import { chosenTheme, darkTheme } from "./theme";
import { GlobalStyles } from "./global";
import { ThemeToggleContext } from "./themeContext";

// Même clé que le script inline de public/index.html, qui pose data-theme avant le rendu.
const THEME_STORAGE_KEY = "theme";

const LIGHT_QUERY = "(prefers-color-scheme: light)";

function readStoredChoice() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch (e) {
    return null;
  }
}

// Réglage de l'appareil : clair s'il le demande, sombre sinon.
function systemMode() {
  return window.matchMedia && window.matchMedia(LIGHT_QUERY).matches
    ? "light"
    : "dark";
}

// Le choix fait avec le bouton prime ; à défaut, on suit l'appareil.
function readInitialMode() {
  return readStoredChoice() || systemMode();
}

function App() {
  const [mode, setMode] = useState(readInitialMode);
  const isDark = mode === "dark";
  const theme = isDark ? darkTheme : chosenTheme;

  // Avant la peinture : les surcharges CSS [data-theme="dark"] basculent
  // dans la même frame que les couleurs portées par le thème.
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  // Tant que le visiteur n'a rien choisi, suivre l'appareil s'il change de mode.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia(LIGHT_QUERY);
    const onChange = () => {
      if (!readStoredChoice()) setMode(systemMode());
    };
    if (query.addEventListener) query.addEventListener("change", onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener)
        query.removeEventListener("change", onChange);
      else query.removeListener(onChange);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setMode((current) => {
      const next = current === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch (e) {
        // stockage indisponible (navigation privée…) : le choix vaut pour la session
      }
      return next;
    });
  }, []);

  const toggleValue = useMemo(() => ({ isDark, toggleTheme }), [
    isDark,
    toggleTheme,
  ]);

  return (
    <BaseProvider theme={isDark ? DarkTheme : LightTheme}>
      <ThemeProvider theme={theme}>
        <ThemeToggleContext.Provider value={toggleValue}>
          <GlobalStyles />
          <div>
            <Main theme={theme} />
          </div>
        </ThemeToggleContext.Provider>
      </ThemeProvider>
    </BaseProvider>
  );
}

export default App;
